import { useMemo } from "react";
import { useData, useDrivers } from "../store/dataStore";
import { ltv, ltvCac, cacPaybackMonths, nrr, grr, magicNumber, burnMultiple } from "../query/metrics";
import { fmtMoney, fmtPct, fmtNum } from "../query/format";
import { KpiCard } from "../components/KpiCard";
import { Drillable } from "../components/Drillable";
import { DriverCell } from "../components/DriverCell";

const SEGMENTS = ["Enterprise", "Mid-Market", "Commercial"];

function segmentEconRow(segment, { accounts, movements, lines, grossMarginPct, cacBySegment }) {
  const accs = accounts.filter((a) => a.segment === segment);
  const arrBase = accs.reduce((s, a) => s + a.arpa, 0) || 1;
  const churnAbs = -movements.filter((m) => m.segment === segment && m.type === "churn").reduce((s, m) => s + m.amount, 0);
  const expAbs = movements.filter((m) => m.segment === segment && m.type === "expansion").reduce((s, m) => s + m.amount, 0);
  const churnPct = (churnAbs / arrBase) * 100;
  const expPct = (expAbs / arrBase) * 100;
  const arpa = accs.length ? arrBase / accs.length : 0;
  const cac = cacBySegment[segment] || 0;
  const ltvV = ltv(arpa, grossMarginPct, churnPct);
  const revenue = lines.filter((l) => l.segment === segment).reduce((s, l) => s + l.amount, 0);
  return {
    segment, accounts: accs.length, arpa, revenue, churnPct, expPct,
    nrr: nrr(churnPct, expPct), grr: grr(churnPct),
    ltv: ltvV, cac, ratio: ltvCac(ltvV, cac), payback: cacPaybackMonths(cac, arpa, grossMarginPct),
  };
}

function computeUnitEcon(tables, grossMarginPct, cacBySegment) {
  const accounts = tables.dim_account || [];
    const movements = tables.fact_arr_movement || [];
    const lines = tables.fact_invoice_line || [];
    const gl = tables.fact_gl || [];

    const segRows = SEGMENTS.map((segment) => {
      const accs = accounts.filter((a) => a.segment === segment);
      const arrBase = accs.reduce((s, a) => s + a.arpa, 0) || 1;
      const churnAbs = -movements.filter((m) => m.segment === segment && m.type === "churn").reduce((s, m) => s + m.amount, 0);
      const expAbs = movements.filter((m) => m.segment === segment && m.type === "expansion").reduce((s, m) => s + m.amount, 0);
      const churnPct = (churnAbs / arrBase) * 100;
      const expPct = (expAbs / arrBase) * 100;
      const arpa = accs.length ? arrBase / accs.length : 0;
      const cac = cacBySegment[segment] || 0;
      const ltvV = ltv(arpa, grossMarginPct, churnPct);
      const revenue = lines.filter((l) => l.segment === segment).reduce((s, l) => s + l.amount, 0);
      return {
        segment, accounts: accs.length, arpa, revenue, churnPct, expPct,
        nrr: nrr(churnPct, expPct), grr: grr(churnPct),
        ltv: ltvV, cac, ratio: ltvCac(ltvV, cac), payback: cacPaybackMonths(cac, arpa, grossMarginPct),
      };
    });

    const totalArr = segRows.reduce((s, r) => s + r.arpa * r.accounts, 0) || 1;
    const w = (fn) => segRows.reduce((s, r) => s + fn(r) * ((r.arpa * r.accounts) / totalArr), 0);
    const blended = { nrr: w((r) => r.nrr), grr: w((r) => r.grr), ratio: w((r) => r.ratio), payback: w((r) => r.payback) };

    const months = [...new Set(movements.map((m) => m.month))].sort();
    const h1 = months.slice(0, Math.floor(months.length / 2));
    const h2 = months.slice(Math.floor(months.length / 2));
    const netNewH2 = movements.filter((m) => h2.includes(m.month)).reduce((s, m) => s + m.amount, 0);
    const smH1 = gl.filter((g) => h1.includes(g.period) && ["Sales", "Marketing"].includes(g.cost_center)).reduce((s, g) => s + g.amount, 0);
    const magic = magicNumber(netNewH2, smH1);

    const opex = gl.filter((g) => g.account_type === "opex").reduce((s, g) => s + g.amount, 0);
    const rev = gl.filter((g) => g.account_type === "revenue").reduce((s, g) => s + g.amount, 0);
    const netNew = movements.reduce((s, m) => s + m.amount, 0);
    const sm = gl.filter((g) => ["Sales", "Marketing"].includes(g.cost_center)).reduce((s, g) => s + g.amount, 0);
    const burn = burnMultiple(opex, rev, netNew, sm);

    return { segRows, blended, magic, burn };
}

export default function UnitEcon() {
  const tables = useData((s) => s.tables);
  const { grossMarginPct, cacBySegment, setDriver, setCac } = useDrivers();
  const { segRows, blended, magic, burn } = useMemo(() => computeUnitEcon(tables, grossMarginPct, cacBySegment), [tables, grossMarginPct, cacBySegment]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>Unit Economics</h2>
        <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
          LTV = (ARPA × GM%) / churn%. Gross margin is a driver:{" "}
          <DriverCell testId="driver-gross-margin" value={grossMarginPct} display={fmtPct(grossMarginPct)} onSave={(v) => setDriver("grossMarginPct", v)} />
          {" "}— edit it and watch LTV/CAC recompute. CAC per segment is editable in the table.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
        <KpiCard testId="kpi-ltv-cac" label="LTV / CAC" value={`${fmtNum(blended.ratio, 1)}x`} status={blended.ratio >= 3 ? "pass" : "watch"} module="unitEcon" seed={[]} />
        <KpiCard testId="kpi-nrr-ue" label="NRR" value={fmtPct(blended.nrr, 1)} status={blended.nrr >= 110 ? "pass" : blended.nrr >= 100 ? "watch" : "miss"} module="arr" seed={[{ field: "type", value: "expansion" }]} />
        <KpiCard testId="kpi-grr" label="GRR" value={fmtPct(blended.grr, 1)} status={blended.grr >= 90 ? "pass" : "watch"} module="arr" seed={[{ field: "type", value: "churn" }]} />
        <KpiCard testId="kpi-payback" label="CAC Payback" value={`${fmtNum(blended.payback, 1)}mo`} status={blended.payback <= 18 ? "pass" : "watch"} module="unitEcon" seed={[]} />
        <KpiCard testId="kpi-magic" label="Magic Number" value={fmtNum(magic, 2)} sub="NetNew H2 / S&M H1" status={magic >= 0.75 ? "pass" : "watch"} module="arr" seed={[]} />
        <KpiCard testId="kpi-burn" label="Burn Multiple" value={`${fmtNum(burn, 2)}x`} sub="S&M ÷ Net New ARR" status={burn <= 1.5 ? "pass" : "watch"} module="pnl" seed={[]} />
      </div>

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="unitecon-table">
          <thead>
            <tr><th>Segment</th><th className="text-right">Accounts</th><th className="text-right">Revenue (invoiced)</th><th className="text-right">ARPA</th><th className="text-right">NRR</th><th className="text-right">LTV</th><th className="text-right">CAC (driver)</th><th className="text-right">LTV/CAC</th><th className="text-right">Payback</th></tr>
          </thead>
          <tbody>
            {segRows.map((r) => (
              <tr key={r.segment}>
                <td>
                  <Drillable module="unitEcon" seed={[{ field: "segment", value: r.segment }]} testId={`drill-seg-${r.segment}`} className="inline-block">
                    <span className="fact-cell font-semibold pr-4">{r.segment}</span>
                  </Drillable>
                </td>
                <td className="text-right num" style={{ color: "var(--slate)" }}>{r.accounts}</td>
                <td className="text-right">
                  <Drillable module="unitEcon" seed={[{ field: "segment", value: r.segment }]} testId={`drill-rev-${r.segment}`} className="inline-block">
                    <span className="fact-cell pr-4">{fmtMoney(r.revenue)}</span>
                  </Drillable>
                </td>
                <td className="text-right num" style={{ color: "var(--navy)" }}>{fmtMoney(r.arpa)}</td>
                <td className="text-right num font-bold" style={{ color: r.nrr >= 110 ? "var(--good)" : r.nrr >= 100 ? "var(--warn)" : "var(--bad)" }}>{fmtPct(r.nrr, 1)}</td>
                <td className="text-right num" style={{ color: "var(--navy)" }}>{fmtMoney(r.ltv)}</td>
                <td className="text-right">
                  <DriverCell testId={`driver-cac-${r.segment}`} value={r.cac} display={fmtMoney(r.cac)} onSave={(v) => setCac(r.segment, v)} />
                </td>
                <td className="text-right num font-bold" style={{ color: r.ratio >= 3 ? "var(--good)" : "var(--warn)" }}>{fmtNum(r.ratio, 1)}x</td>
                <td className="text-right num" style={{ color: "var(--slate)" }}>{fmtNum(r.payback, 1)}mo</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs" style={{ color: "var(--slate)" }}>Facts (drillable ↓): segment names, revenue. Drivers (editable ↑, dashed underline): gross margin, CAC. Drill a segment → cohorts → accounts → invoices → invoice lines.</p>
    </div>
  );
}
